import getApiUrl from "../../../../config/api";

// Every call here goes to backend/domains/history/history_routes.py, takes the
// signed-in user's ID token, and throws when the request fails.

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

async function readJson(res) {
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// ── History ───────────────────────────────────────────────────────────────

/**
 * One page of conversation summaries, newest first.
 *
 * `cursor` is the {ts, id} the previous page returned as next_cursor. The
 * response also carries `folders` and `folder_counts`.
 */
export async function listConversations(token, { limit, q, date, folder, cursor }) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (q) params.set("q", q);
  if (date) params.set("date", date);
  if (folder) params.set("folder", folder);
  if (cursor) {
    params.set("cursor_ts", String(cursor.ts));
    params.set("cursor_id", cursor.id);
  }
  const res = await fetch(`${getApiUrl()}/api/v2/history?${params.toString()}`, {
    headers: authHeaders(token),
  });
  return readJson(res);
}

/** The full ordered message list for one conversation. */
export async function getConversationMessages(token, sessionId) {
  const res = await fetch(`${getApiUrl()}/api/v2/history/${sessionId}`, {
    headers: authHeaders(token),
  });
  return (await readJson(res)).messages;
}

export async function deleteConversation(token, sessionId) {
  const res = await fetch(`${getApiUrl()}/api/v2/history/${sessionId}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
}

/**
 * Set a conversation's user-defined fields. An empty string clears a field,
 * so the row falls back to its first message / no folder.
 */
async function updateConversation(token, sessionId, fields) {
  const res = await fetch(`${getApiUrl()}/api/v2/history/${sessionId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(fields),
  });
  return readJson(res);
}

export const renameConversation = (token, sessionId, title) =>
  updateConversation(token, sessionId, { title });

// ── Folders ───────────────────────────────────────────────────────────────

// Selects the conversations in no folder. Matches UNFILED_SENTINEL in
// history_service.py.
export const UNFILED = "__unfiled__";

/**
 * Every folder in use, with how many conversations each holds, and the total.
 *
 * A one-row unfiltered page: the list response already carries the folder
 * names and counts, and a search or date filter would narrow them.
 */
export async function getFolderInventory(token) {
  const data = await listConversations(token, { limit: 1 });
  return {
    folders: data.folders || [],
    counts: data.folder_counts || {},
    total: typeof data.total === "number" ? data.total : null,
  };
}

/** Move a conversation to `folder`, or out of every folder with "". */
export async function moveToFolder(token, sessionId, folder) {
  const data = await updateConversation(token, sessionId, { folder });
  return data.folder ?? null;
}

// ── Summary ───────────────────────────────────────────────────────────────

/** Generate the LLM recap; `force` writes a new one over an existing one. */
export async function generateSummary(token, sessionId, force) {
  const res = await fetch(`${getApiUrl()}/api/v2/history/${sessionId}/summary`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({ force }),
  });
  return (await readJson(res)).summary;
}

// ── Continue in the chat page ────────────────────────────────────────────

// The page that talks to each agent, by the agentType its answers were saved
// with. Anything else (the CX proxies, or a conversation with no answer yet)
// opens the original chat page.
const CHAT_PAGE_BY_AGENT = {
  root_agent: "/tg-product-assistant",
  faq_agent: "/faq/ai-chat",
};

/**
 * Open a conversation in the chat page of the agent that answered it. That
 * page picks the id up from this localStorage key on load and clears it
 * (AiChat/useChatSession.js, TgProductAssistant.js, FaqChatbot.js).
 */
export function openInChat(navigate, sessionId, language, agentType) {
  localStorage.setItem("sessionId", sessionId);
  navigate({
    pathname: CHAT_PAGE_BY_AGENT[agentType] || "/ai-chat",
    search: `?lang=${language}`,
  });
}
